import { Button, Icon, Modal, type IconName } from '@restomarker/ui-kit';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';

import {
  ALERTS_PATH,
  AlertsNavLink,
  NotificationsDialog,
  useAlertFeedAvailable,
  useNotificationsAvailable,
} from '@/modules/notifications';
import { MorningToggle } from '@/modules/printing';
import { loadKioskBinding, type KioskBinding } from '@/shared/auth';
import { t, type MessageKey } from '@/shared/i18n';

import { useLogout, useSession } from '@/shared/session';
import { setViewMode } from '@/shared/storage';
import { BindKioskDialog } from './BindKioskDialog';
import styles from './kioskNav.module.css';

/**
 * Навигация вида киоска.
 *
 * Layout тот же, что у кабинета (`AppNav`): шапка сверху, слева бургер с названием
 * текущего раздела на узком экране, справа — меню аккаунта. Одна модель навигации
 * на оба вида, и «как отсюда выйти» решается одинаково.
 *
 * Выход/возврат в кабинет — ТОЖЕ видный пункт в полосе, не в меню аккаунта (правка
 * 2026-09, пилот): владелец уже переносил сюда «Настройку принтера» 2026-09-05
 * по тому же мотиву — «должны быть все вверху, в меню, сразу же читаемое и понятное».
 * Тестировщик на пилоте не нашёл «Вернуться в кабинет», спрятанное внизу бургер-панели
 * за именем, — тот же класс дефекта, что и с настройкой принтера тогда, тем же
 * лечением: пункт переехал в саму полосу, рядом с разделами. Имя пользователя
 * («чьей подписью уйдёт этикетка») осталось как подпись — но больше не единственный
 * путь выйти.
 *
 * Раньше на широком экране (планшет в альбомной) меню аккаунта не было ВОВСЕ —
 * CSS показывал его только ≤767px, — и из печати нельзя было ни выйти, ни настроить
 * принтер. Отсюда «никуда из печати нельзя выйти».
 *
 * Нижняя строка меню зависит от роли (T-019):
 * - **сотрудник** — «Выйти»: кабинета у него нет, пересменка = выйти и войти (Д3);
 * - **остальные** — «Выйти из вида»: киоск для них способ отображения.
 *
 * «Закрепить планшет за точкой» (T-040) — тоже в меню аккаунта, и только менеджеру
 * точки и владельцу сети: действие редкое, делается один раз на планшет.
 */
export interface KioskNavProps {
  /**
   * Слот для действия страницы на узком экране — напротив бургера, как в кабинете
   * (`AppNav`). Печать ставит туда «☰ Ещё» (решение владельца 2026-10-03).
   */
  onActionSlot?: (node: HTMLElement | null) => void;
}

export function KioskNav({ onActionSlot }: KioskNavProps = {}): ReactElement {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const navigate = useNavigate();
  const employee = useSession().data ?? null;
  const logout = useLogout();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const [binding, setBinding] = useState(false);
  // Текущее закрепление планшета — ради подписи пункта меню. Без неё пункт «Закрепить»
  // стоял и на уже закреплённом планшете, и менеджер закреплял его заново, не понимая,
  // держится ли первое закрепление.
  const [bound, setBound] = useState<KioskBinding | null>(null);
  // Уведомления в Telegram — повару прежде всего: предупреждение, что у заготовки
  // подходит срок, нужно тому, кто стоит у полки (решение владельца 2026-09-26).
  // Ни бот, ни push не настроены на установке — пункта нет: подключать нечего.
  const [telegramOpen, setTelegramOpen] = useState(false);
  const telegramAvailable = useNotificationsAvailable();
  // Лента точки (T-047): колокольчик ведёт на неё и несёт число непрочитанного. Сервер
  // старше T-047 (404) — колокольчик по-прежнему открывает окно настроек.
  const feedAvailable = useAlertFeedAvailable();

  const kioskOnly = employee?.role === 'employee';
  const canBind = employee?.role === 'location_manager' || employee?.role === 'client_admin';
  const bindLabel = bound
    ? t('kiosk.bind.menuBound').replace('{name}', bound.name)
    : t('kiosk.bind.menu');

  // Перечитывается при закрытии диалога: там закрепляют и открепляют.
  useEffect(() => {
    if (!canBind || binding) return;
    void loadKioskBinding().then(setBound);
  }, [canBind, binding]);
  const current: { label: MessageKey } | undefined =
    pathname === ALERTS_PATH
      ? { label: 'alerts.feed.nav' }
      : ITEMS.find((item) => item.to === pathname);
  const leaveLabel = kioskOnly ? t('nav.logout') : t('kiosk.leaveView');

  function openBinding(): void {
    setMenuOpen(false);
    setBinding(true);
  }

  function handleLogout(): void {
    // Токен стирается ДАЖЕ если сервер не ответил (`onSettled`): человек нажал «Выйти»
    // и обязан выйти. Уход роутером, а не перезагрузкой — полная перезагрузка без сети
    // даёт белый экран (E2E-AUTH-004).
    logout.mutate(undefined, {
      onSettled: () => {
        setConfirmingLogout(false);
        void navigate({ to: '/login', search: { redirect: undefined } });
      },
    });
  }

  async function leaveKiosk(): Promise<void> {
    setMenuOpen(false);
    await setViewMode('cabinet');
    await navigate({ to: '/' });
  }

  function onLeave(): void {
    if (kioskOnly) {
      setMenuOpen(false);
      setConfirmingLogout(true);
      return;
    }
    void leaveKiosk();
  }

  function openTelegram(): void {
    setMenuOpen(false);
    setTelegramOpen(true);
  }

  function goPrinterSetup(): void {
    setMenuOpen(false);
    void navigate({ to: '/kiosk/printer' });
  }

  return (
    <header className={styles.bar}>
      <button
        type="button"
        className={styles.burger}
        aria-expanded={menuOpen}
        aria-controls="kiosk-nav"
        onClick={() => setMenuOpen((value) => !value)}
      >
        <span aria-hidden="true">{menuOpen ? '✕' : '☰'}</span>
        <span className={styles.burgerLabel}>
          {current ? t(current.label) : t('kiosk.nav.label')}
        </span>
      </button>

      <nav
        id="kiosk-nav"
        className={menuOpen ? styles.navOpen : styles.nav}
        aria-label={t('kiosk.nav.label')}
      >
        {ITEMS.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={pathname === item.to ? styles.linkActive : styles.link}
            aria-current={pathname === item.to ? 'page' : undefined}
            onClick={() => setMenuOpen(false)}
          >
            <Icon name={item.icon} size={24} className={styles.linkIcon} />
            {t(item.label)}
          </Link>
        ))}

        {/* Настройка принтера — ВИДНЫЙ пункт в той же полосе, не спрятанный за именем
            (владелец, 2026-09-05). Иконка — шестерёнка того же размера, что у Печати
            и В работе (владелец, 2026-09-21): уменьшенный второй принтер рядом с Печатью
            читался как её дубль и выбивался из ряда. */}
        <button
          type="button"
          className={pathname === '/kiosk/printer' ? styles.linkActive : styles.link}
          aria-current={pathname === '/kiosk/printer' ? 'page' : undefined}
          onClick={goPrinterSetup}
        >
          <Icon name="settings" size={24} className={styles.linkIcon} />
          {t('kiosk.nav.printerSetup')}
        </button>

        {/* Уведомления — тоже видный пункт в полосе: повару его больше негде найти,
            кабинета у него нет. */}
        {feedAvailable ? (
          <AlertsNavLink
            className={styles.link}
            activeClassName={styles.linkActive}
            iconClassName={styles.linkIcon}
            onNavigate={() => setMenuOpen(false)}
          />
        ) : null}
        {!feedAvailable && telegramAvailable ? (
          <button type="button" className={styles.link} onClick={openTelegram}>
            <Icon name="bell" size={24} className={styles.linkIcon} />
            {t('kiosk.nav.telegram')}
          </button>
        ) : null}

        {/* Закрепление планшета за точкой (T-040) — той же природы, что настройка
            принтера строкой выше: редкая настройка УСТРОЙСТВА, и место ей в полосе.
            Не в блоке за именем: `.navAccount` скрыт на широком экране, и пункт,
            оставленный там, пропал бы на моноблоке цеха — видно его было бы только
            под бургером на телефоне. */}
        {canBind ? (
          <button type="button" className={styles.link} onClick={openBinding}>
            <Icon name="key" size={24} className={styles.linkIcon} />
            {bindLabel}
          </button>
        ) : null}

        {/* Вернуться в кабинет / выйти — тем же приёмом, что настройка принтера
            строкой выше: видный пункт в полосе, а не спрятанный внизу бургер-панели
            за именем (см. докстринг компонента). */}
        <button type="button" className={styles.link} onClick={onLeave}>
          <Icon name="chevronLeft" size={24} className={styles.linkIcon} />
          {leaveLabel}
        </button>

        {/* Аккаунт внутри бургера — ТОЛЬКО на узком экране (CSS прячет на широком,
            там та же подпись стоит в полосе справа). Только имя: действие — строкой
            выше, дублировать его здесь незачем. */}
        <div className={styles.navAccount}>
          <span className={styles.navAccountName}>{employee?.full_name ?? ''}</span>
        </div>
      </nav>

      {/* Тумблер «На утро» (T-055) — по центру шапки, только на экране печати и только
          если админ разрешил печать на утро для точки. */}
      {pathname === '/kiosk/print' ? <MorningToggle /> : null}

      {/* Слот действия страницы (узкий экран): кнопка приезжает порталом из страницы,
          см. `PageActions`. */}
      <div className={styles.pageAction} ref={onActionSlot} />

      {/* Подпись «чьей подписью уйдёт этикетка» — на широком экране. Раньше это был
          триггер выпадающего меню; и выход, и закрепление планшета теперь стоят
          видными пунктами в полосе (см. выше), так что меню осталось бы лишним
          слоем над тем, что и так на виду. */}
      <span className={styles.account}>
        <span className={styles.accountName}>{employee?.full_name ?? ''}</span>
      </span>

      {canBind && binding ? (
        <BindKioskDialog
          onClose={() => setBinding(false)}
          onLogout={() => {
            setBinding(false);
            handleLogout();
          }}
        />
      ) : null}

      {telegramOpen ? <NotificationsDialog onClose={() => setTelegramOpen(false)} /> : null}

      {confirmingLogout ? (
        <Modal
          open
          onOpenChange={() => setConfirmingLogout(false)}
          title={t('kiosk.logout.title')}
          description={t('kiosk.logout.description')}
          footer={
            <>
              <Button onClick={() => setConfirmingLogout(false)}>{t('common.cancel')}</Button>
              <Button variant="primary" disabled={logout.isPending} onClick={handleLogout}>
                {t('nav.logout')}
              </Button>
            </>
          }
        />
      ) : null}
    </header>
  );
}

const ITEMS = [
  { to: '/kiosk/print', label: 'kiosk.nav.print', icon: 'print' },
  { to: '/kiosk/labels', label: 'kiosk.nav.board', icon: 'board' },
] as const satisfies readonly { to: string; label: string; icon: IconName }[];
